import { ChevronDown, Loader2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { cn } from 'lib/utils'
import {
  CAROUSEL_DIMENSION_MAX,
  CAROUSEL_DIMENSION_META,
  VIDEO_DIMENSION_MAX,
  VIDEO_DIMENSION_META,
  isCarouselDimensions,
  isVideoDimensions,
  scoreTier,
} from '../../process/creative-process.types'
import type { CreativeProcessSnapshot } from '../../process/creative-process.types'
import {
  designTierLabel,
  tierLabel,
  verdictHeadline,
  type ScoreRubric,
} from '../../process/score-explainers'
import { useCreativeProcessStore } from '../../store/creative-process-store'
import { useGenerationStatusStore } from '../../store/generation-status-store'
import { DimensionBars } from './DimensionBars'
import { FixWithTakaCard } from './FixWithTakaCard'
import { GateChips } from './GateChips'
import { ScoreExplainerProvider } from './ScoreExplainerDialog'
import { useScoreExplainer } from './score-explainer-context'
import { tierColor } from './tier-color'

/** Resolve which rubric a scored snapshot belongs to (drives explainer copy + meta). */
function resolveRubric(dimensions: CreativeProcessSnapshot['dimensions']): ScoreRubric {
  if (!dimensions) return 'static'
  // Video shares hookStrength/cta/finish with carousel, so check video FIRST.
  if (isVideoDimensions(dimensions)) return 'video'
  if (isCarouselDimensions(dimensions)) return 'carousel'
  return 'static'
}

interface ScorePanelProps {
  creativeId: string
  className?: string
  /** Start with the dimension breakdown expanded. Defaults to false (collapsed). */
  defaultExpanded?: boolean
}

/**
 * The always-on quality panel: the SocialKit v2 rubric score (0-100) with its
 * verdict, hard gates, the eleven weighted dimensions, and the single
 * highest-leverage fix. This is the intelligence Taka used to discard — surfaced
 * prominently so the owner sees how strong a post is and exactly why.
 */
export function ScorePanel({ creativeId, className, defaultExpanded = false }: ScorePanelProps) {
  const state = useCreativeProcessStore(s => s.byCreative[creativeId])
  const fetchGeneration = useCreativeProcessStore(s => s.fetchGeneration)
  const genStatus = useGenerationStatusStore(s => s.statuses[creativeId]?.status)
  const [expanded, setExpanded] = useState(defaultExpanded)

  // Hydrate on mount / when the creative changes.
  useEffect(() => {
    fetchGeneration(creativeId)
  }, [creativeId, fetchGeneration])

  // When a background generation finishes, the score has just been written —
  // refetch so the panel fills in without a page reload.
  useEffect(() => {
    if (genStatus === 'ready') fetchGeneration(creativeId)
  }, [genStatus, creativeId, fetchGeneration])

  // The panel always starts COLLAPSED (the verdict line — e.g. "Blocked by a check" — is
  // still visible in the header, so a blocker is signalled without forcing the breakdown
  // open). Expansion is user-driven only. (We used to auto-expand on a gate fail; that was
  // unwanted — the default is a calm, collapsed card.)
  const snapshot = state?.snapshot ?? null
  const rescoring = state?.rescoring ?? false
  const generating = genStatus === 'generating'

  const animatedScore = useCountUp(snapshot?.score ?? 0, state?.previousScore)

  // Nothing to show: a post from a non-scoring engine that's already settled.
  if (!snapshot) {
    if (generating || state?.loading) {
      return (
        <div className={cn('card flex items-center gap-2 p-3 text-xs text-paper-faint', className)}>
          <Loader2 size={14} className="animate-spin" />
          Scoring this post…
        </div>
      )
    }
    return null
  }

  const tier = scoreTier(snapshot.score)
  const color = tierColor(tier)
  const rubric = resolveRubric(snapshot.dimensions)

  return (
    <ScoreExplainerProvider creativeId={creativeId} rubric={rubric} singleFix={snapshot.singleFix}>
      <ScoreCard
        creativeId={creativeId}
        snapshot={snapshot}
        animatedScore={animatedScore}
        color={color}
        rubric={rubric}
        rescoring={rescoring}
        expanded={expanded}
        onToggle={() => setExpanded(e => !e)}
        className={className}
      />
    </ScoreExplainerProvider>
  )
}

interface ScoreCardProps {
  creativeId: string
  snapshot: CreativeProcessSnapshot
  animatedScore: number
  color: string
  rubric: ScoreRubric
  rescoring: boolean
  expanded: boolean
  onToggle: () => void
  className?: string
}

/** The card itself. Lives under ScoreExplainerProvider so the ring and verdict can
 *  open their explainers; gate chips and dimension bars use the context directly. */
function ScoreCard({
  creativeId,
  snapshot,
  animatedScore,
  color,
  rubric,
  rescoring,
  expanded,
  onToggle,
  className,
}: ScoreCardProps) {
  const { openExplainer } = useScoreExplainer()
  const showFix = Boolean(snapshot.singleFix) && snapshot.verdict !== 'SHIP'
  const tier = scoreTier(snapshot.score)
  const verdict = snapshot.verdict ? String(snapshot.verdict) : ''

  return (
    <div className={cn('card flex flex-col gap-3 p-3', rescoring && 'animate-pulse', className)}>
      {/* Collapsed headline: tapping anywhere on the header expands/collapses the
          breakdown. The score ring and the verdict stay precise "tap to learn"
          targets (they stop the click from also toggling). The chevron is the
          accessible toggle control. */}
      <div
        onClick={onToggle}
        className="group -m-1 flex cursor-pointer items-center gap-3 rounded-md p-1 select-none"
      >
        <button
          type="button"
          onClick={e => {
            e.stopPropagation()
            openExplainer('concept.quality')
          }}
          className="flex-shrink-0 cursor-help rounded-full transition-opacity hover:opacity-80"
          aria-label="What the Quality score means"
        >
          <ScoreRing score={animatedScore} color={color} />
        </button>

        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          {verdict ? (
            <button
              type="button"
              onClick={e => {
                e.stopPropagation()
                openExplainer(`verdict.${verdict}`)
              }}
              aria-label={`What "${verdictHeadline(verdict)}" means`}
              className="min-w-0 cursor-help truncate text-left text-sm font-semibold text-paper underline decoration-[color:var(--paper-faint)] decoration-dotted underline-offset-2 hover:opacity-80"
            >
              {verdictHeadline(verdict)}
            </button>
          ) : (
            <span style={{ color }} className="text-sm font-semibold">
              {tierLabel(tier)}
            </span>
          )}
          {rescoring && (
            <Loader2 size={12} className="flex-shrink-0 animate-spin text-paper-faint" />
          )}
        </div>

        <button
          type="button"
          onClick={e => {
            e.stopPropagation()
            onToggle()
          }}
          className="flex-shrink-0 text-paper-faint transition-colors group-hover:text-paper"
          aria-label={expanded ? 'Collapse breakdown' : 'Expand breakdown'}
          aria-expanded={expanded}
        >
          <ChevronDown size={18} className={cn('transition-transform', expanded && 'rotate-180')} />
        </button>
      </div>

      {expanded && (
        <>
          {/* Hard checks: the four pass/fail gates, labeled so they read as checks. */}
          {snapshot.gates && (
            <div className="flex flex-col gap-2 border-t border-line pt-3">
              <p className="mono text-[10px] tracking-[0.2em] text-paper-faint uppercase">
                Quality checks
              </p>
              <GateChips gates={snapshot.gates} />
            </div>
          )}

          {/* Art-direction tier, relabeled in plain words and tappable. Separate from
              the score, so a high score next to "looks templated" makes sense. */}
          {snapshot.designTier && (
            <button
              type="button"
              onClick={() => openExplainer(`design.${snapshot.designTier}`)}
              aria-label={`What the design tier "${designTierLabel(snapshot.designTier)}" means`}
              className="flex w-fit cursor-help items-center gap-1.5 text-[11px] text-paper-dim hover:text-paper"
            >
              <span className="mono tracking-[0.15em] text-paper-faint uppercase">Design</span>
              <span className="rounded-md bg-bg-3 px-1.5 py-0.5 font-medium underline decoration-[color:var(--paper-faint)] decoration-dotted underline-offset-2">
                {designTierLabel(snapshot.designTier)}
              </span>
            </button>
          )}

          {snapshot.dimensions && (
            <div className="flex flex-col gap-2 border-t border-line pt-3">
              <p className="mono text-[10px] tracking-[0.2em] text-paper-faint uppercase">
                Dimensions
              </p>
              {rubric === 'video' ? (
                <DimensionBars
                  dimensions={snapshot.dimensions as unknown as Record<string, number>}
                  meta={VIDEO_DIMENSION_META}
                  max={VIDEO_DIMENSION_MAX}
                  rubric="video"
                />
              ) : rubric === 'carousel' ? (
                <DimensionBars
                  dimensions={snapshot.dimensions as unknown as Record<string, number>}
                  meta={CAROUSEL_DIMENSION_META}
                  max={CAROUSEL_DIMENSION_MAX}
                  rubric="carousel"
                />
              ) : (
                <DimensionBars dimensions={snapshot.dimensions} rubric="static" />
              )}
            </div>
          )}
          {showFix && snapshot.singleFix && (
            <FixWithTakaCard creativeId={creativeId} singleFix={snapshot.singleFix} />
          )}
        </>
      )}
    </div>
  )
}

/** Compact circular score ring with the number centered. */
function ScoreRing({ score, color }: { score: number; color: string }) {
  const size = 52
  const stroke = 4
  const r = (size - stroke) / 2
  const c = 2 * Math.PI * r
  const pct = Math.max(0, Math.min(100, score)) / 100
  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--bg-3)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - pct)}
          style={{ transition: 'stroke-dashoffset 0.4s ease' }}
        />
      </svg>
      <span
        className="absolute inset-0 flex items-center justify-center text-base font-bold tabular-nums"
        style={{ color }}
      >
        {Math.round(score)}
      </span>
    </div>
  )
}

/** Tween a number from `from` (or 0) to `target` over ~600ms on change. */
function useCountUp(target: number, from?: number): number {
  const [value, setValue] = useState(target)
  const rafRef = useRef<number | null>(null)

  useEffect(() => {
    const start = from ?? value
    if (start === target) {
      setValue(target)
      return
    }
    const duration = 600
    const t0 = performance.now()
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / duration)
      const eased = 1 - Math.pow(1 - p, 3)
      setValue(start + (target - start) * eased)
      if (p < 1) rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, from])

  return value
}
